import * as THREE from 'three';
import { Entity } from '../../entities';
import { createMatteMaterial, fbm3d } from '../../visual';
import { getSharedNoduleGeometry } from '../sector/OreGeometryCache';
import type { ItemDefinition, ItemId } from '../inventory';
import type { WorldResourceEntry } from '../sector';
import type { SurfaceObstacle } from './SurfaceObstacle';

export interface OreDepositOptions {
  readonly position: THREE.Vector3;
  readonly resource: WorldResourceEntry;
  readonly definition: ItemDefinition;
  readonly size: number;
  readonly tint: number;
}

/**
 * Visual style of the ore exposed on a deposit. `metal` mirrors the gold
 * asteroid: solid metallic chunks with a tiny emissive bias. `crystal`
 * mirrors the plasma/ice/quartz asteroid: faceted shards that glow in
 * the item's accent colour. The same rock body is used for both.
 */
type OreStyle = 'metal' | 'crystal';

// Items that should read as a worked metal — flat shading, high
// metalness, almost no emission. Everything else falls back to `crystal`
// (faceted, emissive shards) which fits gases, ices and minerals.
const METAL_ITEMS: ReadonlySet<ItemId> = new Set<ItemId>([
  'gold-ore',
  'silver',
  'iron-scrap',
  'stellar-iron',
  'alien-alloy'
]);

const _tmpDir = new THREE.Vector3();

/**
 * Mineable surface deposit. Visually the same idea as the ore-bearing
 * asteroids: a chunky rock with a few partially-buried ore nuggets
 * sticking out of it. Yields and the planet pool come from
 * `WorldResources`. No persistence — the planet scene resets on exit.
 */
export class OreDeposit extends Entity {
  readonly resource: WorldResourceEntry;
  readonly definition: ItemDefinition;
  readonly collisionRadius: number;
  readonly maxHealth: number;
  readonly obstacle: SurfaceObstacle;
  health: number;

  private hitFlash = 0;
  private rockMaterial: THREE.MeshStandardMaterial | null = null;
  private oreMaterial: THREE.MeshStandardMaterial | null = null;
  private secondaryOreMaterial: THREE.MeshStandardMaterial | null = null;
  private orePulseMaterials: THREE.MeshBasicMaterial[] = [];
  private pulseTime = Math.random() * Math.PI * 2;

  constructor(options: OreDepositOptions) {
    super();
    this.resource = options.resource;
    this.definition = options.definition;
    this.collisionRadius = options.size * 0.95;
    // Health = a couple of hits at FPS_BEAM_DPS rates; rare nodes are a
    // touch tougher so they read as "valuable".
    this.maxHealth = Math.max(3, Math.round(options.resource.yieldMax * 2.4));
    this.health = this.maxHealth;

    this.object3D.name = `OreDeposit:${options.resource.itemId}`;
    this.object3D.position.copy(options.position);
    this.object3D.rotation.set(
      Math.random() * Math.PI,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI
    );

    this.buildMesh(options);

    // Obstacle entry shared with the surface obstacle list. Position is
    // shared by reference so movement (none, today) would propagate.
    this.obstacle = {
      position: this.object3D.position,
      radius: this.collisionRadius,
      // Deposit height is roughly the rock size; the ship can fly above
      // it without an invisible bump.
      height: options.size * 1.1
    };
  }

  takeDamage(amount: number): boolean {
    if (amount <= 0) return false;
    this.health = Math.max(0, this.health - amount);
    this.hitFlash = 0.18;
    return this.health <= 0;
  }

  override update(dt: number): void {
    if (this.hitFlash > 0) {
      this.hitFlash = Math.max(0, this.hitFlash - dt);
      const flash = (this.hitFlash / 0.18) * 0.85;
      if (this.rockMaterial) this.rockMaterial.emissiveIntensity = flash;
      if (this.oreMaterial) {
        this.oreMaterial.emissiveIntensity =
          this.oreMaterial.userData['baseEmissive'] + flash * 1.4;
      }
      if (this.secondaryOreMaterial) {
        this.secondaryOreMaterial.emissiveIntensity =
          this.secondaryOreMaterial.userData['baseEmissive'] + flash;
      }
    } else if (this.orePulseMaterials.length > 0) {
      // Gentle breathing on the additive halos, like the asteroid ore
      // pulse — keeps the deposit alive at a glance without distracting.
      this.pulseTime += dt * 1.8;
      const pulse = 0.78 + Math.sin(this.pulseTime) * 0.18;
      for (const mat of this.orePulseMaterials) {
        mat.opacity = pulse * (mat.userData['baseOpacity'] ?? 0.35);
      }
    }
  }

  private buildMesh(options: OreDepositOptions): void {
    const rockColor = options.tint;
    const oreColor = parseHexColor(options.definition.accent);
    const style: OreStyle = METAL_ITEMS.has(options.definition.id)
      ? 'metal'
      : 'crystal';

    const rockMat = createMatteMaterial({
      color: rockColor,
      roughness: 0.95,
      flatShading: true,
      emissive: oreColor,
      emissiveIntensity: 0
    });
    this.rockMaterial = rockMat;
    const rock = buildRock(options.size, rockMat);
    this.object3D.add(rock);

    // Ore deposits — same idea as the asteroid `buildGoldOre` /
    // `buildCrystalOre` builders: a few partially-buried nuggets that
    // poke out of the rock surface. Number, material and shape vary by
    // style so metals look like worked metal and crystals like glowing
    // shards.
    const depositCount = 3 + Math.floor(Math.random() * 3);

    if (style === 'metal') {
      const metalMat = new THREE.MeshStandardMaterial({
        color: oreColor,
        metalness: 1.0,
        roughness: 0.32,
        flatShading: true,
        emissive: oreColor,
        emissiveIntensity: 0.05
      });
      metalMat.userData['baseEmissive'] = 0.05;
      this.oreMaterial = metalMat;

      // Slightly darker satellite chunks read as exposed seam material —
      // same trick the gold asteroid uses to break the silhouette.
      const darkMetalMat = new THREE.MeshStandardMaterial({
        color: shadeHex(oreColor, 0.55),
        metalness: 0.9,
        roughness: 0.55,
        flatShading: true,
        emissive: oreColor,
        emissiveIntensity: 0.0
      });
      darkMetalMat.userData['baseEmissive'] = 0.0;
      this.secondaryOreMaterial = darkMetalMat;

      for (let i = 0; i < depositCount; i++) {
        this.placeOreCluster(options.size, metalMat, darkMetalMat);
      }
    } else {
      const crystalMat = createMatteMaterial({
        color: oreColor,
        roughness: 0.45,
        emissive: oreColor,
        emissiveIntensity: 0.85,
        flatShading: true
      });
      crystalMat.toneMapped = false;
      crystalMat.userData['baseEmissive'] = 0.85;
      this.oreMaterial = crystalMat;

      const dimCrystalMat = createMatteMaterial({
        color: shadeHex(oreColor, 0.6),
        roughness: 0.55,
        emissive: oreColor,
        emissiveIntensity: 0.4,
        flatShading: true
      });
      dimCrystalMat.toneMapped = false;
      dimCrystalMat.userData['baseEmissive'] = 0.4;
      this.secondaryOreMaterial = dimCrystalMat;

      for (let i = 0; i < depositCount; i++) {
        this.placeOreCluster(options.size, crystalMat, dimCrystalMat);
      }

      // Subtle additive halo around the cluster — same intent as the
      // asteroid ice/plasma halo: reads from a distance without becoming
      // a "transparent mountain". Small radius, low opacity. Crystal
      // deposits only; metallic chunks sell themselves on reflection.
      const haloMat = new THREE.MeshBasicMaterial({
        color: oreColor,
        transparent: true,
        opacity: 0.32,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false
      });
      haloMat.userData['baseOpacity'] = 0.32;
      this.orePulseMaterials.push(haloMat);
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(options.size * 0.55, 12, 10),
        haloMat
      );
      halo.position.y = options.size * 0.25;
      this.object3D.add(halo);
    }
  }

  /** Place a single ore cluster (one main nugget + 0-2 satellites) on
   *  the surface of the rock. Mirrors how `buildGoldOre` / `buildIronOre`
   *  position their chunks on asteroids — direction is random, offset is
   *  `size - mainSize * fraction` so the nugget sits half-buried. */
  private placeOreCluster(
    size: number,
    mainMat: THREE.Material,
    satelliteMat: THREE.Material
  ): void {
    const group = new THREE.Group();
    group.renderOrder = 2;

    const mainSize = size * (0.22 + Math.random() * 0.12);
    const main = new THREE.Mesh(getSharedNoduleGeometry(), mainMat);
    main.scale.setScalar(mainSize);
    main.renderOrder = 2;
    group.add(main);

    const satellites = Math.random() < 0.85 ? 1 + Math.floor(Math.random() * 2) : 0;
    for (let j = 0; j < satellites; j++) {
      const r = mainSize * (0.45 + Math.random() * 0.3);
      const chunk = new THREE.Mesh(
        getSharedNoduleGeometry(),
        j === 0 ? mainMat : satelliteMat
      );
      chunk.scale.setScalar(r);
      chunk.renderOrder = 2;
      chunk.position.set(
        (Math.random() - 0.5) * mainSize * 1.4,
        (Math.random() * 0.5 + 0.1) * mainSize,
        (Math.random() - 0.5) * mainSize * 1.4
      );
      group.add(chunk);
    }

    const direction = randomUnitVector();
    const offset = size - mainSize * (0.4 + Math.random() * 0.25);
    group.position.copy(direction).multiplyScalar(offset);
    group.rotation.set(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2
    );
    this.object3D.add(group);
  }
}

function parseHexColor(value: string): number {
  const cleaned = value.startsWith('#') ? value.slice(1) : value;
  return parseInt(cleaned, 16) || 0xffffff;
}

/** Linearly multiply each channel of a colour by `amount`. Used to derive
 *  a darker satellite-chunk shade from the primary ore colour without
 *  requiring a second hex per item. */
function shadeHex(color: number, amount: number): number {
  const r = Math.max(0, Math.min(255, Math.round(((color >> 16) & 0xff) * amount)));
  const g = Math.max(0, Math.min(255, Math.round(((color >> 8) & 0xff) * amount)));
  const b = Math.max(0, Math.min(255, Math.round((color & 0xff) * amount)));
  return (r << 16) | (g << 8) | b;
}

function randomUnitVector(): THREE.Vector3 {
  const theta = Math.random() * Math.PI * 2;
  const phi = Math.acos(2 * Math.random() - 1);
  return new THREE.Vector3(
    Math.sin(phi) * Math.cos(theta),
    Math.sin(phi) * Math.sin(theta),
    Math.cos(phi)
  );
}

/** Build a chunky rock mesh — same FBM-displaced icosahedron approach the
 *  surface scatter uses, kept independent so deposits can stand out as
 *  larger and rougher than the ambient pebbles. */
function buildRock(size: number, material: THREE.Material): THREE.Mesh {
  const geometry = new THREE.IcosahedronGeometry(size, 2);
  const position = geometry.attributes['position'] as THREE.BufferAttribute;
  const seedX = Math.random() * 30;
  const seedY = Math.random() * 30;
  const seedZ = Math.random() * 30;
  const axis = new THREE.Vector3(
    0.85 + Math.random() * 0.25,
    0.55 + Math.random() * 0.25,
    0.85 + Math.random() * 0.25
  );
  for (let i = 0; i < position.count; i++) {
    _tmpDir
      .set(position.getX(i), position.getY(i), position.getZ(i))
      .normalize();
    const n = fbm3d(
      _tmpDir.x * 1.4 + seedX,
      _tmpDir.y * 1.4 + seedY,
      _tmpDir.z * 1.4 + seedZ,
      3
    );
    const d = 1 + n * 0.28;
    const r = size * d;
    position.setXYZ(
      i,
      _tmpDir.x * axis.x * r,
      _tmpDir.y * axis.y * r,
      _tmpDir.z * axis.z * r
    );
  }
  position.needsUpdate = true;
  geometry.computeVertexNormals();
  return new THREE.Mesh(geometry, material);
}
